/**
 * 役种判定主引擎
 *
 * 流程：
 *   1. 特殊牌形优先（国士/七对子 → 直接判役满或七对子）
 *   2. 普通形：检查所有可能的役种
 *   3. 役种互斥处理（如 二盃口 排除 一盃口；四暗刻 排除 三暗刻/对对和）
 *   4. 宝牌计算（宝牌/里宝牌/赤宝牌，不算役但加番）
 */

import {
  Counts,
  NUM_TILE_TYPES,
  countsTotal,
  indexToName,
  isHonor,
} from '../tile.js';
import type { Decomposition } from '../agari.js';
import {
  countAnkou,
  countDora,
  countIipeikou,
  countKantsu,
  checkChanta,
  checkChuuren,
  checkDragons,
  checkFlush,
  checkIttsu,
  checkJunseiChuuren,
  checkPinfu,
  checkRyuuiisou,
  checkSanshokuDoujun,
  checkSanshokuDoukou,
  checkSuukantsu,
  checkTanyao,
  checkToitoi,
  checkTsuuiisou,
  checkWinds,
  findYakuhai,
  isKokushi13,
  isKoutsu,
  isShuntsu,
} from './checkers.js';
import * as Y from './defs.js';
import {
  makeYaku,
  type WinContext,
  type Yaku,
  type YakuContext,
  type YakuDef,
  type YakuResult,
  type Wind,
} from './types.js';

// ════════════════════════════════════════════════════════════════
// 风 → 牌索引
// ════════════════════════════════════════════════════════════════

const WIND_INDEX: Record<Wind, number> = {
  east: 27,
  south: 28,
  west: 29,
  north: 30,
};

// ════════════════════════════════════════════════════════════════
// 主入口
// ════════════════════════════════════════════════════════════════

/**
 * 判定役种
 *
 * @param ctx 判定上下文（含分解、手牌、场况）
 * @returns 役种结果（含番数、宝牌）
 */
export function detectYaku(ctx: YakuContext): YakuResult {
  const { decomposition: d, context: c, isClosed, winTile } = ctx;

  // ── 役满优先 ──
  const yakuman = detectYakuman(ctx);
  if (yakuman.length > 0) {
    const yakumanCount = yakuman.reduce((sum, y) => {
      // 累计倍数：役满 = 1，双倍役满 = 2
      return sum + (y.def.level === 'double-yakuman' ? 2 : 1);
    }, 0);
    return {
      yaku: yakuman,
      han: 0,
      doraHan: 0, // 役满不计宝牌
      totalHan: 0,
      hasYakuman: true,
      yakumanCount,
      isYakuless: false,
    };
  }

  // ── 普通役 ──
  const yaku: Yaku[] = [];

  // 特殊牌形
  if (d.form === 'chiitoitsu') {
    yaku.push(makeYaku(Y.CHIITOITSU, true));
  } else if (d.form === 'kokushi') {
    // 已在役满处理，不会到这里
  } else {
    // 普通形
    collectNormalYaku(ctx, yaku);
  }

  // 立直系（七对子/国士也适用）
  collectRiichiYaku(ctx, yaku);

  // 去重 + 互斥处理
  const finalYaku = resolveExclusions(yaku, ctx);

  const han = finalYaku.reduce((s, y) => s + y.han, 0);

  // 宝牌（有役才计）
  let doraHan = 0;
  if (finalYaku.length > 0 && c.dora) {
    doraHan += countDora(ctx.allTiles, c.dora.doraIndicators);
    if (isClosed && c.isRiichi && c.dora.uraIndicators) {
      doraHan += countDora(ctx.allTiles, c.dora.uraIndicators);
    }
    doraHan += c.dora.redFives ?? 0;
  }

  return {
    yaku: finalYaku,
    han,
    doraHan,
    totalHan: han + doraHan,
    hasYakuman: false,
    yakumanCount: 0,
    isYakuless: finalYaku.length === 0,
  };
}

// ════════════════════════════════════════════════════════════════
// 普通形役种收集
// ════════════════════════════════════════════════════════════════

function collectNormalYaku(ctx: YakuContext, yaku: Yaku[]): void {
  const { decomposition: d, allTiles, isClosed, winTile, context: c } = ctx;
  const isTsumo = c.winMethod === 'tsumo';

  // ── 断幺九 ──
  if (checkTanyao(allTiles)) {
    yaku.push(makeYaku(Y.TANYAO, isClosed));
  }

  // ── 役牌 ──
  const roundWindIdx = WIND_INDEX[c.roundWind];
  const seatWindIdx = WIND_INDEX[c.seatWind];
  const yakuhaiTiles = findYakuhai(d, roundWindIdx, seatWindIdx);

  for (const t of yakuhaiTiles) {
    if (t === 31) yaku.push(makeYaku(Y.YAKUHAI_HAKU, isClosed));
    else if (t === 32) yaku.push(makeYaku(Y.YAKUHAI_HATSU, isClosed));
    else if (t === 33) yaku.push(makeYaku(Y.YAKUHAI_CHUN, isClosed));
    else {
      // 风牌：场风与自风各计 1 番；二者为同一刻子时（連風牌）两役并存 → 共 2 番
      // （天凤/雀魂/riichienv 均按 2 番；旧实现 t!==roundWindIdx 守卫导致漏计自风）
      if (t === roundWindIdx) yaku.push(makeYaku(Y.YAKUHAI_ROUND, isClosed));
      if (t === seatWindIdx) yaku.push(makeYaku(Y.YAKUHAI_SEAT, isClosed));
    }
  }

  // ── 平和（仅门清）──
  if (isClosed && c.winTile !== undefined) {
    const isRyanmen = checkRyanmenWait(ctx);
    const yakuhaiSet = [31, 32, 33, roundWindIdx, seatWindIdx];
    if (checkPinfu(d, isRyanmen, d.pair, yakuhaiSet)) {
      yaku.push(makeYaku(Y.PINFU, true));
    }
  }

  // ── 一杯口 / 二盃口（仅门清）──
  if (isClosed) {
    const pairs = countIipeikou(d);
    if (pairs >= 4) {
      yaku.push(makeYaku(Y.RYANPEIKOU, true));
    } else if (pairs >= 2) {
      yaku.push(makeYaku(Y.IIPEIKOU, true));
    }
  }

  // ── 三色同顺 ──
  if (checkSanshokuDoujun(d)) {
    yaku.push(makeYaku(Y.SANSHOKU_DOUJUN, isClosed));
  }

  // ── 一气通贯 ──
  if (checkIttsu(d)) {
    yaku.push(makeYaku(Y.ITTSU, isClosed));
  }

  // ── 全带系（混全带/纯全带/混老头）──
  const chanta = checkChanta(d, allTiles);
  if (chanta === 'honroutou') {
    yaku.push(makeYaku(Y.HONROUTOU, isClosed));
  } else if (chanta === 'junchan') {
    yaku.push(makeYaku(Y.JUNCHAN, isClosed));
  } else if (chanta === 'chanta') {
    yaku.push(makeYaku(Y.CHANTA, isClosed));
  }

  // ── 对对和 ──
  if (checkToitoi(d)) {
    yaku.push(makeYaku(Y.TOITOI, isClosed));
  }

  // ── 三暗刻 ──
  const ankou = countAnkou(d, winTile, isTsumo);
  if (ankou >= 3) {
    yaku.push(makeYaku(Y.SANANKOU, isClosed));
  }

  // ── 三色同刻 ──
  if (checkSanshokuDoukou(d)) {
    yaku.push(makeYaku(Y.SANSHOKU_DOUKOU, isClosed));
  }

  // ── 三杠子 ──
  if (countKantsu(d) === 3) {
    yaku.push(makeYaku(Y.SANKANTSU, isClosed));
  }

  // ── 小三元 ──
  const dragons = checkDragons(d);
  if (dragons === 'shousangen') {
    yaku.push(makeYaku(Y.SHOUSHANGEN, isClosed));
  }

  // ── 混一色 / 清一色 ──
  const flush = checkFlush(allTiles);
  if (flush === 'chinitsu') {
    yaku.push(makeYaku(Y.CHINITSU, isClosed));
  } else if (flush === 'honitsu') {
    yaku.push(makeYaku(Y.HONITSU, isClosed));
  }
}

/** 立直系役种（门清限定） */
function collectRiichiYaku(ctx: YakuContext, yaku: Yaku[]): void {
  const { isClosed, context: c } = ctx;
  if (!isClosed) return;

  if (c.isTenhou) {
    // 天和已在役满处理
    return;
  }
  if (c.isChiihou) return;

  if (c.isDoubleRiichi) {
    yaku.push(makeYaku(Y.DOUBLE_RIICHI, true));
  } else if (c.isRiichi) {
    yaku.push(makeYaku(Y.RIICHI, true));
  }

  if ((c.isRiichi || c.isDoubleRiichi) && c.isIppatsu) {
    yaku.push(makeYaku(Y.IPPATSU, true));
  }

  // 门前清自摸和
  if (c.winMethod === 'tsumo') {
    yaku.push(makeYaku(Y.MENZEN_TSUMO, true));
  }

  // 海底/河底/岭上/抢杠
  if (c.isHaitei && c.winMethod === 'tsumo') {
    yaku.push(makeYaku(Y.HAITEI, true));
  }
  if (c.isHoutei && c.winMethod === 'ron') {
    yaku.push(makeYaku(Y.HOUTEI, true));
  }
  if (c.isRinshan) {
    yaku.push(makeYaku(Y.RINSHAN, true));
  }
  if (c.isChankan) {
    yaku.push(makeYaku(Y.CHANKAN, true));
  }
}

// ════════════════════════════════════════════════════════════════
// 役满判定
// ════════════════════════════════════════════════════════════════

function detectYakuman(ctx: YakuContext): Yaku[] {
  const { decomposition: d, allTiles, isClosed, winTile, context: c, counts } = ctx;
  const result: Yaku[] = [];
  const isTsumo = c.winMethod === 'tsumo';

  // ── 国士无双 ──
  if (d.form === 'kokushi') {
    // 判断是否十三面（和牌前 13 种各 1 张）
    const before = [...counts];
    before[winTile]--;
    if (isKokushi13(before)) {
      result.push(makeYaku(Y.KOKUSHI_13, true));
    } else {
      result.push(makeYaku(Y.KOKUSHI, true));
    }
    return result;
  }

  // ── 天和 / 地和 ──
  if (c.isTenhou && isClosed) {
    result.push(makeYaku(Y.TENHOU, true));
  }
  if (c.isChiihou && isClosed && isTsumo) {
    result.push(makeYaku(Y.CHIIHOU, true));
  }

  // ── 字一色 ──
  if (checkTsuuiisou(allTiles)) {
    result.push(makeYaku(Y.TSUUIISOU, isClosed));
  }

  // ── 清老头 ──
  if (checkChinroutouSafe(allTiles)) {
    result.push(makeYaku(Y.CHINROUTOU, isClosed));
  }

  // ── 绿一色 ──
  if (checkRyuuiisou(allTiles)) {
    result.push(makeYaku(Y.RYUUISOU, isClosed));
  }

  // ── 九莲宝灯（门清 + 单一花色）──
  if (isClosed && checkChuuren(allTiles)) {
    const before = [...counts];
    before[winTile]--;
    if (checkJunseiChuuren(allTiles, winTile, before)) {
      result.push(makeYaku(Y.JUNSEI_CHUUREN, true));
    } else {
      result.push(makeYaku(Y.CHUUREN, true));
    }
  }

  // ── 大三元 ──
  const dragons = checkDragons(d);
  if (dragons === 'daisangen') {
    result.push(makeYaku(Y.DAISANGEN, isClosed));
  }

  // ── 大四喜 / 小四喜 ──
  const winds = checkWinds(d);
  if (winds === 'daisuushii') {
    result.push(makeYaku(Y.DAISUUSHII, isClosed));
  } else if (winds === 'shousuushii') {
    result.push(makeYaku(Y.SHOUSUUSHII, isClosed));
  }

  // ── 四暗刻（门清）──
  if (isClosed) {
    const ankou = countAnkou(d, winTile, isTsumo);
    if (ankou >= 4) {
      // 单骑听牌 → 双倍役满
      if (d.pair === winTile && countInHand(counts, winTile) === 2) {
        result.push(makeYaku(Y.SUUANKOU_TANKI, true));
      } else {
        result.push(makeYaku(Y.SUUANKOU, true));
      }
    }
  }

  // ── 四杠子 ──
  if (checkSuukantsu(d)) {
    result.push(makeYaku(Y.SUUKANTSU, isClosed));
  }

  return result;
}

/** 清老头（避免命名冲突的包装） */
function checkChinroutouSafe(allTiles: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if (allTiles[i] === 0) continue;
    if (isHonor(i)) return false;
    const num = (i % 9) + 1;
    if (num !== 1 && num !== 9) return false;
  }
  return true;
}

/** 手牌中某种牌的张数 */
function countInHand(counts: Counts, tile: number): number {
  return counts[tile] ?? 0;
}

// ════════════════════════════════════════════════════════════════
// 互斥 / 去重
// ════════════════════════════════════════════════════════════════

/**
 * 处理役种之间的互斥与去重
 *
 * 主要规则：
 *   · 二盃口 排除 一盃口
 *   · 四暗刻 排除 三暗刻 / 对对和
 *   · 大三元 排除 小三元 / 役牌
 *   · 大四喜 排除 小四喜 / 风牌役
 *   · 字一色 排除 混一色 / 清一色
 *   · 混老头 排除 混全带 / 对对和 / 三暗刻（按高番优先）
 *   · 同 ID 只保留一次
 */
function resolveExclusions(yaku: readonly Yaku[], ctx: YakuContext): Yaku[] {
  const byId = new Map<string, Yaku>();
  for (const y of yaku) {
    if (y.han <= 0) continue; // 副露后失效的役
    if (!byId.has(y.def.id)) byId.set(y.def.id, y);
  }

  const ids = new Set(byId.keys());

  // 二盃口 → 去一盃口
  if (ids.has('ryanpeikou')) byId.delete('iipeikou');

  // 四暗刻已由役满路径处理，这里处理对对和与三暗刻的互斥
  // （对对和 + 三暗刻可共存，不互斥）

  // 字一色 → 去 混一色/清一色/混老头/对对和
  if (ids.has('tsuuiisou')) {
    byId.delete('honitsu');
    byId.delete('chinitsu');
    byId.delete('honroutou');
    byId.delete('toitoi');
    byId.delete('chanta');
  }

  // 清老头 → 去 混老头/纯全带/对对和
  if (ids.has('chinroutou')) {
    byId.delete('honroutou');
    byId.delete('junchan');
    byId.delete('toitoi');
  }

  // 大三元 → 去 小三元 + 三元牌役
  if (ids.has('daisangen')) {
    byId.delete('shousangen');
    byId.delete('yakuhai_haku');
    byId.delete('yakuhai_hatsu');
    byId.delete('yakuhai_chun');
  }

  // 小三元 与 役牌（三元牌刻子）【叠加】——
  //   标准规则：小三元(2 番) + 各三元牌刻子的役牌(各 1 番) 同时计（riichienv/天凤 一致）。
  //   旧实现误删三元牌役 → 少 2 番；已修。

  // 大四喜 → 去 小四喜 + 风牌役 + 对对和
  if (ids.has('daisuushii')) {
    byId.delete('shousuushii');
    byId.delete('yakuhai_round');
    byId.delete('yakuhai_seat');
    byId.delete('toitoi');
  }

  // 小四喜 → 去 风牌役
  if (ids.has('shousuushii')) {
    byId.delete('yakuhai_round');
    byId.delete('yakuhai_seat');
  }

  // 混一色 → 保留（可与役牌共存）
  // 清一色 → 去 混一色（不共存，正常不会同时命中）

  // 平和 + 对对和互斥（平和要求全顺子，对对和要全刻子，天然互斥）
  // 平和 + 一杯口 可共存 ✓

  const result = [...byId.values()];

  // 去重：同一役牌（场风=自风时）只算一次
  return result;
}

// ════════════════════════════════════════════════════════════════
// 两面听判定（平和用）
// ════════════════════════════════════════════════════════════════

/**
 * 判断是否两面听
 *
 * 和了牌必须是在某个顺子的**端点**，且该顺子可以两侧延展：
 *   · 和了牌作顺子最小牌 → 原手牌是 (start+1, start+2)，且 start+2 不是 9（否则是边张）
 *   · 和了牌作顺子最大牌 → 原手牌是 (start, start+1)，且 start 不是 1（否则是边张）
 */
function checkRyanmenWait(ctx: YakuContext): boolean {
  // 高点法的「待ち读法」覆盖：仅两面读法成立平和；单骑/坎张/边张一律不成立
  const rd = ctx.context.waitReading;
  if (rd) return rd === 'ryanmen';

  const { decomposition: d, winTile, counts } = ctx;
  const before = countInHand(counts, winTile);

  for (const m of d.mentsu) {
    if (!isShuntsu(m) || m.open) continue;
    const start = m.start;
    const end = start + 2;

    if (winTile === start) {
      // 和了牌是最小牌 → 原手牌含 (start+1, start+2)
      const nextInHand = before >= 1; // 已含和了牌
      const face = (end % 9) + 1;
      // 边张：原手牌是 89 听 7 → 顺子 7-8-9，和了牌 7 是最小
      if (face === 9) return false;
      if (nextInHand) return true;
    }
    if (winTile === end) {
      const face = (start % 9) + 1;
      // 边张：原手牌是 12 听 3 → 顺子 1-2-3，和了牌 3 是最大
      if (face === 1) return false;
      return true;
    }
  }
  return false;
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 役种列表 → 可读字符串 */
export function formatYaku(yaku: readonly Yaku[]): string {
  return yaku.map((y) => `${y.def.name}(${y.han}番)`).join(' ');
}

/** 役种结果 → 摘要 */
export function summarizeYaku(r: YakuResult): string {
  const parts: string[] = [];
  if (r.hasYakuman) {
    parts.push(`役满×${r.yakumanCount}`);
    parts.push(formatYaku(r.yaku));
  } else {
    parts.push(formatYaku(r.yaku));
    if (r.doraHan > 0) parts.push(`宝牌(${r.doraHan}番)`);
    parts.push(`= ${r.totalHan}番`);
  }
  return parts.join(' ');
}
